import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@sbd-doug-frontend/app/environments/environment';
import { map, Observable, of } from 'rxjs';
import {
  Task,
  TaskListResponse,
  TaskResponse,
  TasksListItem,
  TaskUILookupResponse,
  TaskUILookupResponseDataUILookups,
} from './tasks.types';
import {
  Task as AccountUserTask,
} from '@sbd-doug-frontend/app/modules/accounts/accounts-manager/acccount-user-list/account-user-vtabs/account-user-tasks/tasksets-list/tasksets.types';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class TasksService {
  private _httpClient = inject(HttpClient);

  tasks = signal<TasksListItem[]>([]);
  totalTasks = signal<number>(0);
  uiLookups = signal<TaskUILookupResponseDataUILookups>(null);
  task = signal<Task>(null);


  getTasks(filters: any = {}): Observable<TasksListItem[]> {
    const params = filters || {};

    return this._httpClient
      .get<TaskListResponse>(
        `${environment.apiBaseUrl}/api/v4/tasks`, {
          params: params,
        },
      ).pipe(
        map((response) => {
          this.tasks.set(response.data.tasks.tasks);
          this.totalTasks.set(response.data.tasks.total_tasks);
          return response.data.tasks.tasks;
        }),
      );
  }
  getUILookups(): Observable<TaskUILookupResponseDataUILookups> {
    if (this.uiLookups() !== null) {
      return of(this.uiLookups());
    }

    return this._httpClient
      .get<TaskUILookupResponse>(
        `${environment.apiBaseUrl}/api/v4/tasks/ui-lookups`,
      ).pipe(
        map((response) => {
          this.uiLookups.set(response.data.ui_lookups);
          return response.data.ui_lookups;
        }),
      );
  }
  getTask(taskID: number): Observable<Task> {
    return this._httpClient
      .get<TaskResponse>(
        `${environment.apiBaseUrl}/api/v4/task/${taskID}`,
        {
          params: {
            env: 'v4',
          },
        },
      )
      .pipe(
        map((response) => {
          const t = response.data.task.task;
          const dd = new Date(t.task_due_date.toString());
          const currentTask = {
            ...t,
            task_due_date: dd,
            task_due_time: dd.toTimeString().slice(0, 5),
          };
          this.task.set(currentTask);
          return currentTask;
        }),
      );
  }

  _cathyService = inject(CathyService);
  create(task: Task): Observable<TaskResponse> {
    return this._httpClient.post<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/create-task-for-entity`, task).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          return response;
        },
      ),
    );
  }
  createTaskMessage(task: Task): Observable<TaskResponse> {
    return this._httpClient.post<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/create-task-message`, task).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          return response;
        },
      ),
    );
  }

  reOpenTask(taskId: number): Observable<TaskResponse> {
    return this._httpClient.put<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${taskId}/reopen-task`, {}).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }

          const t = response.data.task.task;
          const dd = new Date(t.task_due_date.toString());
          const task = {
            ...t,
            task_due_date: dd,
            task_due_time: dd.toTimeString().slice(0, 5),
          };

          this.task.set(task);
          const tasks_ = { ...this.tasks(), task };
          this.tasks.set(tasks_);
          this._cathyService.success(response.message);
          return response;
        },
      ),
    );
  }

  formatTime(dateString?: string | null): string | null {
    if (!dateString) return null;

    const dateObject = new Date(dateString);
    if (isNaN(dateObject.getTime())) return null; // Handle invalid date cases

    const hours = dateObject.getHours().toString().padStart(2, '0');
    const minutes = dateObject.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  updateTask(task: Task | FormData): Observable<Task> {
    const taskId = task instanceof FormData ? task.get('task_id') : task.task_id;
    return this._httpClient.put<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${taskId}`, task).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          const t = response.data.task.task;
          const dd = new Date(t.task_due_date.toString());
          const updatedTask = <Task>{
            ...t,
            task_due_date: dd,
            task_due_time: dd.toTimeString().slice(0, 5),
          };
          this.task.set(updatedTask);
          this._cathyService.success(response.message);
          return response.data.task.task;
        },
      ),
    );
  }

  cancelTask(task: Task | AccountUserTask): Observable<Task> {
    return this._httpClient.put<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${task.task_id}/cancel-task`, task).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          const updatedTask = response.data.task.task;
          this.task.set(updatedTask);

          const tasks_ = <TasksListItem[]>(this.tasks().map(task_ =>
            +updatedTask.task_id === +task_.task_id ? { ...task_, ...updatedTask } : task_,
          ));

          this.tasks.set(tasks_);
          this._cathyService.success(response.message);
          return response.data.task.task;
        },
      ),
    );
  }

  completeTask(task: Task | AccountUserTask): Observable<Task> {
    return this._httpClient.put<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${task.task_id}/complete-task`, task).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          this._cathyService.success(response.message);
          return response.data.task.task;
        },
      ),
    );
  }


  clearTask() {
    this.task.set(null);
  }

  deleteTaskFile(task_id: number, file_id: number): Observable<TaskResponse> {
    return this._httpClient
      .get<TaskResponse>(
        `${environment.apiBaseUrl}/api/v4/task/${task_id}/detach-file/${file_id}`,
      ).pipe(
        map((response) => {
          this.task.set(response.data.task.task);
          return response;
        }),
      );
  }

  makeTasksListItemFromTask(task: Task): TasksListItem {
    return {
      task_id: task.task_id,
      task_type_id: task.task_type_id,
      task_status_id: task.task_status_id,
      task_label: task.task_label,
      task_description: task.task_description,
      task_due_date: task.task_due_date || null,
      task_start_date: task.task_start_date || null,
      task_completion_date: task.task_completion_date || null,
      task_cancellation_date: task.task_cancellation_date || null,
      task_type_code: task.task_type_code,
      task_info: task.task_info,
      task_history: task.task_history,
    };
  }

  createFollowupTask(task_id: number) {
    return this._httpClient.get<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${task_id}/followup-task`).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          return response;
        },
      ),
    );
  }


}
